import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Cpu, 
  Zap, 
  Clock, 
  Settings2,
  GitMerge,
  MessageSquare,
  ArrowRight,
  TrendingUp,
  Activity,
  CheckCircle2,
  ShoppingBag,
  RotateCcw,
  Mail,
  RefreshCw,
  Inbox
} from 'lucide-react';
import './Automations.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/followups');
const getToken = () => localStorage.getItem('access_token') || '';


// The API only returns id/name/active_users/conv_rate/type, so color and subtext
// (which don't have a backend source) are resolved locally by id, preserving the
// original design's per-touchpoint styling and descriptions.
const TIMELINE_STYLE_MAP = {
  soft_nudge: {
    color: 'purple',
    subtext: 'Dynamic 10% discount offered via personal DM.',
  },
  final_incentive: {
    color: 'emerald',
    subtext: 'Free Express Shipping + FOMO stock alert.',
  },
  email_discount: {
    color: 'orange',
    subtext: 'Automated high-conversion discount code delivered via priority email sequence.',
  },
  post_purchase: {
    color: 'cyan',
    subtext: 'Thank you note + request for review in exchange for credit.',
  },
  abandoned_offer: {
    color: 'rose',
    subtext: 'Final chance DM for unused dynamic offers expiring soon.',
  },
};

const getTimelineStyle = (id) => TIMELINE_STYLE_MAP[id] || { color: 'blue', subtext: '' };


const Automations = () => {
  const [selectedFlow, setSelectedFlow] = useState('');
  const [timelineData, setTimelineData] = useState([]);
  const [automationsList, setAutomationsList] = useState([]);
  const [flowStepsList, setFlowStepsList] = useState([]);
  const [recommendation, setRecommendation] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.timeline)
        ? data.timeline
        : Array.isArray(data?.strategies)
          ? data.strategies
          : [];
      setTimelineData(list);
      setAutomationsList(Array.isArray(data?.automations) ? data.automations : []);
      setFlowStepsList(Array.isArray(data?.flow_steps) ? data.flow_steps : []);
      setRecommendation(data?.recommendation || '');
    } catch (err) {
      console.error('Automations timeline fetch error:', err);
      setError('Unable to load follow-up timeline data from the server.');
      setTimelineData([]);
      setAutomationsList([]);
      setFlowStepsList([]);
      setRecommendation('');
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);


  const getTimelineIcon = (label = '') => {
    const l = label.toLowerCase();
    if (l.includes('purchase')) return ShoppingBag;
    if (l.includes('abandoned')) return RotateCcw;
    if (l.includes('email')) return Mail;
    return Clock;
  };


  // Merge live API data with the local color/subtext lookup
  const timelineCards = timelineData.map((item) => {
    const style = getTimelineStyle(item.id);
    return {
      time: item.type || '—',
      label: item.name || 'Untitled Touchpoint',
      subtext: style.subtext,
      color: style.color,
      metrics: {
        active: (item.active_users ?? 0).toLocaleString(),
        conv: item.conv_rate ?? 0,
      },
    };
  });

  const hasTimelineData = timelineCards.length > 0;


  return (
    <>
      <Helmet>
        <title>Automations - Revexia AI Revenue Brain</title>
        <meta name="description" content="AI-powered automation sequences and workflows" />
      </Helmet>


      <div className="automations-page-wrapper">
        <div className="container-fluid px-4">
          {/* Strategy Timelines */}
          <section className="timelines-section">
            <div className="section-header-with-icon">
              <Activity size={20} className="section-icon cyan" />
              <h3 className="section-main-title">Strategy Timelines</h3>
              <button
                className="automation-settings-btn ms-auto"
                onClick={fetchTimeline}
                disabled={loading}
                aria-label="Refresh timeline data"
                style={{ marginLeft: 'auto' }}
              >
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
              </button>
            </div>

            {error && (
              <div className="fallback-notice">
                <Inbox size={14} />
                <span>{error}</span>
              </div>
            )}

            {loading ? (
              <div className="automation-management-card">
                <div className="loading-spinner"></div>
                <p>Loading timeline data...</p>
              </div>
            ) : !hasTimelineData ? (
              <div className="no-data-block">
                <Inbox size={26} />
                <p>No follow-up timeline data available</p>
              </div>
            ) : (
              <div className="row g-4">
                {timelineCards.map((card, index) => {
                  const Icon = getTimelineIcon(card.label);
                  return (
                    <div key={index} className="col-12 col-md-6 col-xl-4">
                      <div className={`timeline-card timeline-${card.color}`}>
                        <div className="timeline-card-header">
                          <div className={`timeline-card-icon ${card.color}`}>
                            <Icon size={18} />
                          </div>
                          <span className="timeline-card-time">{card.time}</span>
                        </div>
                        <div className="timeline-card-body">
                          <h4 className="timeline-card-title">{card.label}</h4>
                          <p className="timeline-card-subtext">{card.subtext}</p>
                        </div>
                        <div className="timeline-card-footer">
                          <div className="timeline-metric">
                            <p className="timeline-metric-label">Active Users</p>
                            <p className="timeline-metric-value">{card.metrics.active}</p>
                          </div>
                          <div className="timeline-divider"></div>
                          <div className="timeline-metric">
                            <p className="timeline-metric-label">Conv. Rate</p>
                            <p className={`timeline-metric-value timeline-${card.color}`}>{card.metrics.conv}%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>


          {/* Main Control Grid */}
          <div className="row g-4 mb-5">
            {/* Automation List */}
            <div className="col-12 col-lg-8">
              <div className="automation-management-card">
                <div className="automation-management-header">
                  <div>
                    <h3 className="automation-management-title">Management Engine</h3>
                    <p className="automation-management-subtitle">Configure and monitor active AI sequences</p>
                  </div>
                  <button className="automation-settings-btn">
                    <Settings2 size={18} />
                  </button>
                </div>
                <div className="automation-table-wrapper">
                  <table className="automation-table">
                    <thead>
                      <tr>
                        <th>Automation / History</th>
                        <th>Trigger Type</th>
                        <th>Status</th>
                        <th className="text-end">ROI Uplift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {automationsList.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4 text-muted">
                            <Inbox size={20} className="me-2 mb-1" />
                            No automation sequences available
                          </td>
                        </tr>
                      ) : (
                        automationsList.map((auto) => (
                          <tr 
                            key={auto.name} 
                            onClick={() => setSelectedFlow(auto.name)}
                            className={`automation-table-row ${selectedFlow === auto.name ? 'active-flow' : ''}`}
                          >
                            <td>
                              <p className="automation-name">{auto.name}</p>
                              <p className="automation-last-active">Active: {auto.lastActive || auto.last_active || '—'}</p>
                            </td>
                            <td>
                              <div className="automation-trigger">
                                <Zap size={14} className="trigger-icon" /> {auto.trigger}
                              </div>
                            </td>
                            <td>
                              <span className={`automation-status-badge status-${(auto.status || '').toLowerCase()}`}>
                                {auto.status}
                              </span>
                            </td>
                            <td className="text-end">
                              <p className={`automation-performance ${(auto.performance || '').startsWith('+') ? 'positive' : 'neutral'}`}>
                                {auto.performance || '—'}
                              </p>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>


            {/* Visual Flow Preview */}
            <div className="col-12 col-lg-4">
              <div className="logic-canvas-card">
                <div className="logic-canvas-header">
                  <div className="logic-canvas-icon">
                    <GitMerge size={20} />
                  </div>
                  <h3 className="logic-canvas-title">Logic Canvas</h3>
                </div>


                <div className="flow-steps-container">
                  <div className="flow-steps-line"></div>
                  {flowStepsList.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <Inbox size={20} className="me-2 mb-1" />
                      No logic steps configured
                    </div>
                  ) : (
                    flowStepsList.map((step, i) => {
                      const StepIcon = step.icon === 'Zap' ? Zap : step.icon === 'Cpu' ? Cpu : step.icon === 'MessageSquare' ? MessageSquare : CheckCircle2;
                      return (
                        <div key={i} className="flow-step-item">
                          <div className={`flow-step-icon ${step.color || 'blue'}`}>
                            <StepIcon size={16} />
                          </div>
                          <div className="flow-step-content">
                            <p className="flow-step-label">{step.label}</p>
                            <p className="flow-step-value">{step.val || step.value}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>


                <button className="simulation-mode-btn">
                  <span>Simulation Mode</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>


          {/* Recommendations Footer */}
          <div className="row g-4">
            <div className="col-12 col-lg-8">
              <div className="ai-recommendation-card">
                <div className="ai-recommendation-icon">
                  <TrendingUp size={20} />
                </div>
                <div className="ai-recommendation-content">
                  <p className="ai-recommendation-label">AI Recommendation Engine</p>
                  <p className="ai-recommendation-text">
                    {recommendation || "No active automation recommendations at this time."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


export default Automations;